import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuthContext } from './context/AuthContext';
import { useApi } from './hooks/useApi';
import { useLocalStorage } from './hooks/useLocalStorage';
import { ROUTES } from './utils/constants';
import AdminDashboard from './pages/AdminDashboard/AdminDashboard.jsx';
import apiService from './services/api';

// Components
import Header from './components/common/Header/Header';
import Footer from './components/common/Footer/Footer';
import ChatWidget from './components/common/ChatWidget/ChatWidget';
import StoriesSection from './components/StoriesSection/StoriesSection';

// Modals
import LoginModal from './components/modals/LoginModal/LoginModal';
import VerificationModal from './components/modals/VerificationModal/VerificationModal';
// ⛔ JOBS DISABLED: import PostJobModal from './components/modals/PostJobModal/PostJobModal';
import ProductDetailModal from './components/modals/ProductDetailModal/ProductDetailModal';
import OAuthCallback from './components/modals/OAuthCallback/OAuthCallback';

// ✅ NEW: PostAdFlow replaces PostAdModal + PremiumPlansPage combo
import PostAdFlow from './components/modals/PostAdModal/PostAdFlow';

// Pages
import HomePage from './pages/HomePage/HomePage';
import ShopsPage from './pages/ShopsPage/ShopsPage';
// ⛔ JOBS DISABLED: import JobsPage from './pages/JobsPage/JobsPage';
import PlayToWinPage from './pages/PlayToWinPage/PlayToWinPage';
import WalletPage from './pages/WalletPage/WalletPage';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import AppViewPage from './pages/AppViewPage/AppViewPage';

// Assets
import logo from './assets/logo.png';
import headerBg from './assets/header_background.png.png';

// Styles
import './App.css';

// ── Constants ──────────────────────────────────────────────────────────────────
const VALID_ROUTES = Object.values(ROUTES);
const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// ── Capture ?ad= IMMEDIATELY at module load, before any React renders ──────────
// React StrictMode double-mounts, useLocalStorage re-renders, and effects all
// run AFTER this line — so this is the only safe place to read the URL param.
const INITIAL_AD_ID = new URLSearchParams(window.location.search).get('ad');

// Module-level flag — survives React StrictMode's double unmount/remount
let urlRestoredFlag = false;

// ── URL helpers ────────────────────────────────────────────────────────────────
const getAdIdFromUrl = () =>
  new URLSearchParams(window.location.search).get('ad');

const slugify = (text) =>
  String(text || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

const pushAdToUrl = (adId, productName) => {
  const url = new URL(window.location.href);
  url.searchParams.set('ad', adId);
  if (productName) url.searchParams.set('name', slugify(productName));
  window.history.pushState({ adId }, '', url.toString());
};

const clearAdFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('ad');
  url.searchParams.delete('name');
  window.history.replaceState({}, '', url.toString());
};

// Cleans up ?profile_tab= when leaving the profile page
const clearProfileTabFromUrl = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('profile_tab');
  window.history.replaceState({}, '', url.toString());
};

// ── Recommendation API helper ──────────────────────────────────────────────────
async function fetchRecommendedAds() {
  try {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('token')     ||
      sessionStorage.getItem('authToken') ||
      sessionStorage.getItem('token')   ||
      '';

    if (!token) return null;

    const response = await fetch(`${API_BASE}/ads/recommended/`, {
      headers: { 'Authorization': `Token ${token}` },
      cache: 'no-store',
    });

    if (!response.ok) {
      console.warn(`⚠️ /ads/recommended/ returned ${response.status} — falling back to /ads/`);
      return null;
    }

    const _feedType = response.headers.get('X-Feed-Type') || 'unknown'; // eslint-disable-line no-unused-vars
    const _feedUser = response.headers.get('X-Feed-User') || 'unknown'; // eslint-disable-line no-unused-vars
    const _feedCats = response.headers.get('X-Feed-Categories') || 'none'; // eslint-disable-line no-unused-vars

    const data = await response.json();
    const raw  = Array.isArray(data) ? data
               : data?.results        ? data.results
               : data?.ads            ? data.ads
               : null;

    if (!raw) { console.warn('⚠️ /ads/recommended/ returned unexpected shape — falling back'); return null; }


    return raw.map(ad => apiService.transformAdData(ad));
  } catch (err) {
    console.warn('⚠️ fetchRecommendedAds error — falling back to /ads/:', err.message);
    return null;
  }
}

// ── AppContent ─────────────────────────────────────────────────────────────────
function AppContent() {
  const { isAuthenticated, isAdmin, logout, authVersion } = useAuthContext();

  const [currentPage, setCurrentPage] = useLocalStorage('currentPage', ROUTES.HOME);
  const [products, setProducts] = useState([]);
  // ⛔ JOBS DISABLED: const [jobs, setJobs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const [loginModalOpen,        setLoginModalOpen]        = useState(false);
  const [verificationModalOpen, setVerificationModalOpen] = useState(false);
  // ⛔ JOBS DISABLED: const [postJobModalOpen, setPostJobModalOpen] = useState(false);
  // ⚡ Open modal shell immediately if URL has ?ad= — shows skeleton while product loads
  const [productDetailOpen,     setProductDetailOpen]     = useState(!!INITIAL_AD_ID);
  const [selectedProduct,       setSelectedProduct]       = useState(null);
  const [loginIdentifier,       setLoginIdentifier]       = useState('');

  // ✅ NEW: Single boolean controls the entire Post Ad flow (plan picker → form)
  const [postAdFlowOpen, setPostAdFlowOpen] = useState(false);
  // ✅ Skeleton loading state — true while modal is open but product not yet loaded
  const [isModalLoading, setIsModalLoading] = useState(!!INITIAL_AD_ID);

  const { loading, fetchAds } = useApi(); // ⛔ JOBS DISABLED: fetchJobs removed

  const [initialLoadDone, setInitialLoadDone] = useState(false); // ✅ useState so effects re-run when data loads

  // ── Guard invalid stored route ─────────────────────────────────────────────
  useEffect(() => {
    if (!VALID_ROUTES.includes(currentPage)) setCurrentPage(ROUTES.HOME);
  }, [currentPage, setCurrentPage]);

  // ── Suspension check — runs on mount, focus, and tab visibility change ─────
  // Independent of Header so it works on every page including Admin dashboard.
  // If is_active === false the account was suspended — clear session immediately.
  useEffect(() => {
    const checkSuspension = async () => {
      if (!isAuthenticated) return;
      const loginTime = parseInt(localStorage.getItem('loginTime') || '0'); // eslint-disable-line no-unused-vars
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('authToken') || '';
      if (!token) return;
      try {
        const res = await fetch(`${API_BASE}/auth/profile/`, {
          headers: { Authorization: `Token ${token}` },
        });

        // ✅ KEY FIX: Django's TokenAuthentication returns 401 when is_active=False.
        // The profile endpoint never returns is_active=false in the body because
        // the request is rejected before it reaches the view.
        // So we treat 401 = suspended/invalid session → force logout.
        if (res.status === 401 || res.status === 403) {
          console.warn('🔒 Session rejected (401/403) — account suspended or token invalid, forcing logout.');
          logout();
          setCurrentPage(ROUTES.HOME);
          return;
        }

        if (res.ok) {
          const data = await res.json();
          // Belt-and-suspenders: also check is_active in body in case
          // a custom auth backend allows the request through
          if (data.is_active === false) {
            console.warn('🔒 Account suspended (is_active=false) — forcing logout.');
            logout();
            setCurrentPage(ROUTES.HOME);
          }
        }
      } catch (err) {
        console.warn('Suspension check failed (non-fatal):', err.message);
      }
    };

    // 1. Run on mount / whenever auth changes
    setTimeout(checkSuspension, 5000);

    // 2. Run when user returns to this window from another app
    window.addEventListener('focus', checkSuspension);

    // 3. Run when user switches back to this tab
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkSuspension();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('focus', checkSuspension);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, logout, setCurrentPage]);

  // ── Clean up URL params when switching pages ───────────────────────────────
  useEffect(() => {
    if (currentPage !== ROUTES.PROFILE) {
      clearProfileTabFromUrl(); // leaving profile → remove ?profile_tab=
    }
    if (currentPage !== ROUTES.HOME && !productDetailOpen) {
      clearAdFromUrl(); // leaving home with no modal → remove ?ad= & ?name=
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // ── Open modal skeleton IMMEDIATELY if URL has ?ad= ────────────────────────
  // Step 1: open skeleton modal right away — user sees it instantly (zero wait)
  // Step 2: fetch ONLY that one ad via /api/ads/<id>/ — 1 record, very fast
  // Step 3: loadData() runs in parallel for the full feed (doesn't block modal)
  // If the direct fetch fails/times out the URL restoration effect picks it up
  // from the feed once loadData() finishes.
  useEffect(() => {

    if (INITIAL_AD_ID && !urlRestoredFlag) {
      // ── Open skeleton right away ──────────────────────────────────────────
      setIsModalLoading(true);
      setProductDetailOpen(true);

      // ── Fetch single ad directly via /api/ads/<id>/ ───────────────────────
      // This hits a single-record endpoint and is much faster than the full feed.
      const token =
        localStorage.getItem('token') ||
        localStorage.getItem('token')     ||
        sessionStorage.getItem('authToken') ||
        sessionStorage.getItem('token')   || '';

      const headers = token ? { 'Authorization': `Token ${token}` } : {};

      const singleAdFetch = fetch(`${API_BASE}/ads/${INITIAL_AD_ID}/`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(data => data ? apiService.transformAdData(data) : null);

      const timeout = new Promise(resolve => setTimeout(() => resolve(null), 4000));

      Promise.race([singleAdFetch, timeout]).then(product => {
        if (product && !urlRestoredFlag) {
          setSelectedProduct(product);
          setIsModalLoading(false);
          pushAdToUrl(INITIAL_AD_ID, product.name || product.title);
          urlRestoredFlag = true;
        } else if (!product) {
          console.warn('⚠️ Direct fetch timed out or failed — skeleton stays until feed loads');
        }
      }).catch(() => {
        console.warn('⚠️ Direct fetch error — skeleton stays until feed loads');
      });

      // ── Load full feed in parallel (does NOT block modal) ─────────────────
      loadData();

    } else {
      loadData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Re-fetch ads when auth changes ────────────────────────────────────────
  useEffect(() => {
    if (authVersion === undefined || authVersion === 0) return;
    const refetch = async () => {
      try {
        if (isAuthenticated) {
          const recommended = await fetchRecommendedAds();
          if (recommended && recommended.length > 0) {
            const _s2 = new Set();
            setProducts(recommended.filter(a => { if (_s2.has(a.id)) return false; _s2.add(a.id); return true; }));
            return;
          }
        }
        const adsData = await fetchAds();
        const _s3 = new Set();
        setProducts(adsData.filter(a => { if (_s3.has(a.id)) return false; _s3.add(a.id); return true; }));
      } catch (err) {
        console.error('❌ Re-fetch after auth change failed:', err);
      }
    };
    setTimeout(refetch, 800);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authVersion, isAuthenticated]);

  // ── Restore product modal from ?ad= URL ───────────────────────────────────
  // Uses INITIAL_AD_ID captured at module load — NOT getAdIdFromUrl() inside
  // the effect — because by the time effects run, React StrictMode double-mount
  // and useLocalStorage re-renders may have already wiped window.location.search.
  useEffect(() => {

    if (urlRestoredFlag || !initialLoadDone) {
      return;
    }

    if (products.length > 0) {
      const product = products.find(p => String(p.id) === String(INITIAL_AD_ID));
      if (product) {
        setSelectedProduct(product); setProductDetailOpen(true);
        setIsModalLoading(false); // ✅ hide skeleton
        pushAdToUrl(INITIAL_AD_ID, product.name || product.title); // re-stamp URL with name slug
        urlRestoredFlag = true; return;
      } else {
      }
    }
    // Only fetch if there is a real ad ID — prevents /api/ads/null/ 404
    if (INITIAL_AD_ID) fetchProductFromApi(INITIAL_AD_ID);
    else urlRestoredFlag = true; // nothing to restore
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length, initialLoadDone]);

  const fetchProductFromApi = async (adId) => {
    try {
      if (!adId || adId === 'null') {
        clearAdFromUrl(); setProductDetailOpen(false); setIsModalLoading(false);
        urlRestoredFlag = true; return;
      }
      const transformedData = await apiService.getAdById(adId);
      if (!transformedData) { clearAdFromUrl(); setProductDetailOpen(false); setIsModalLoading(false); urlRestoredFlag = true; return; }
      setSelectedProduct(transformedData); setProductDetailOpen(true);
      setIsModalLoading(false); // ✅ hide skeleton
      urlRestoredFlag = true;
    } catch (error) {
      console.error('❌ Error fetching product:', error);
      clearAdFromUrl(); setProductDetailOpen(false); setIsModalLoading(false); urlRestoredFlag = true;
    }
  };

  useEffect(() => {
    const onPopState = (e) => {
      const adId = getAdIdFromUrl();
      if (!adId) { setProductDetailOpen(false); setSelectedProduct(null); }
      else if (e.state?.adId) {
        const product = products.find(p => String(p.id) === String(e.state.adId));
        if (product) { setSelectedProduct(product); setProductDetailOpen(true); }
        else fetchProductFromApi(e.state.adId);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [products]);

  // ── Data loader ────────────────────────────────────────────────────────────
  const loadData = async () => {
    try {

      // ⚡ Load ads feed (Jobs disabled)
      const adsData = await (async () => {
        const recommended = await fetchRecommendedAds();
        if (recommended && recommended.length > 0) {
          return recommended;
        }
        const ads = await fetchAds();
        return ads;
      })();

      const _seen = new Set();
      setProducts(adsData.filter(ad => { if (_seen.has(ad.id)) return false; _seen.add(ad.id); return true; }));
      // ⛔ JOBS DISABLED: setJobs(jobsData);
      setInitialLoadDone(true);
      return adsData;
    } catch (error) {
      console.error('❌ Error loading data:', error);
      setInitialLoadDone(true); // ✅ FIX: even on error, allow URL restoration to attempt
    }
  };

  // ── Modal handlers ─────────────────────────────────────────────────────────
  const openLoginModal        = () => setLoginModalOpen(true);
  const closeLoginModal       = () => setLoginModalOpen(false);

  const openVerificationModal = (identifier) => {
    setLoginIdentifier(identifier);
    setVerificationModalOpen(true);
  };
  const closeVerificationModal = () => setVerificationModalOpen(false);

  // ✅ UPDATED: openPostAdModal now opens PostAdFlow directly
  // PostAdFlow handles the two steps internally:
  //   Step 1 — PlanSelectionModal (with promo badges)
  //   Step 2 — PostAdModal (form, with ← Change Plan button)
  const openPostAdModal = () => {
    if (!isAuthenticated) { openLoginModal(); return; }
    setPostAdFlowOpen(true);
  };

  // ⛔ JOBS DISABLED: const openPostJobModal = () => { ... };
  // ⛔ JOBS DISABLED: const closePostJobModal = () => setPostJobModalOpen(false);

  const openProductDetail = (product) => {
    setSelectedProduct(product); setProductDetailOpen(true);
    pushAdToUrl(product.id, product.name || product.title);
  };
  const closeProductDetail = () => {
    setProductDetailOpen(false); setSelectedProduct(null); clearAdFromUrl();
  };
  const openProductById = (productId) => { // eslint-disable-line no-unused-vars
    const product = products.find(p => p.id === productId);
    if (product) openProductDetail(product);
  };

  const handleSearchFocus  = () => document.querySelector('[data-category-section]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const handleScrollToTop  = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const getRelatedProducts = (currentProduct) => !currentProduct ? [] : products.filter(p => p.category === currentProduct.category && p.id !== currentProduct.id).slice(0, 6);
  const handleAdminLogout  = () => { logout(); setCurrentPage(ROUTES.HOME); };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="App" style={{ position: 'relative' }}>
      {currentPage !== ROUTES.ADMIN && (
        <Header
          logo={logo}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          handleSearchFocus={handleSearchFocus}
          handleScrollToTop={handleScrollToTop}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          openLoginModal={openLoginModal}
          openPostAdModal={openPostAdModal}
        />
      )}

      {currentPage === ROUTES.HOME && <StoriesSection />}

      {currentPage === ROUTES.HOME && (
        <HomePage
          products={products}
          loading={loading}
          searchTerm={searchTerm}
          activeCategory={activeCategory}
          setActiveCategory={setActiveCategory}
          handleSearchFocus={handleSearchFocus}
          openProductDetail={openProductDetail}
          openPostAdModal={openPostAdModal}
          headerBg={headerBg}
        />
      )}
      {currentPage === ROUTES.SHOPS      && <ShopsPage />}
      {/* ⛔ JOBS DISABLED: currentPage === ROUTES.JOBS && <JobsPage jobs={jobs} openPostJobModal={openPostJobModal} /> */}
      {currentPage === ROUTES.PLAY_TO_WIN && <PlayToWinPage />}
      {currentPage === ROUTES.WALLET     && <WalletPage />}
      {currentPage === ROUTES.PROFILE    && <ProfilePage />}
      {currentPage === ROUTES.APP_VIEW   && (
        <AppViewPage
          products={products}
          onClose={() => setCurrentPage(ROUTES.HOME)}
          onOpenDetail={openProductDetail}
          openLoginModal={openLoginModal}
        />
      )}
      {currentPage === ROUTES.ADMIN && isAdmin && (
        <AdminDashboard onLogout={handleAdminLogout} />
      )}

      {/* ✅ NEW: PostAdFlow — single component handles plan selection + ad form */}
      <PostAdFlow
        isOpen={postAdFlowOpen}
        onClose={() => setPostAdFlowOpen(false)}
        onAdPosted={loadData}
        noTrigger
      />

      <LoginModal
        isOpen={loginModalOpen}
        onClose={closeLoginModal}
        onOpenVerification={openVerificationModal}
        setCurrentPage={setCurrentPage}
      />

      <VerificationModal
        isOpen={verificationModalOpen}
        onClose={closeVerificationModal}
        loginIdentifier={loginIdentifier}
        setLoginIdentifier={setLoginIdentifier}
        setCurrentPage={setCurrentPage}
      />

      {/* ⛔ JOBS DISABLED: */}
      {/* ⛔ JOBS DISABLED: <PostJobModal isOpen={postJobModalOpen} onClose={closePostJobModal} onJobPosted={loadData} /> */}
     

      <ProductDetailModal
        isOpen={productDetailOpen}
        product={selectedProduct}
        isLoading={isModalLoading}
        onClose={closeProductDetail}
        getRelatedProducts={getRelatedProducts}
        onProductClick={openProductDetail}
        isLoggedIn={isAuthenticated}
        onOpenLoginModal={openLoginModal}
      />

      {currentPage !== ROUTES.ADMIN && currentPage !== ROUTES.APP_VIEW && (
        <>
          <ChatWidget />
          <Footer />
        </>
      )}
    </div>
  );
}

// ── App root ───────────────────────────────────────────────────────────────────
function App() {
  if (window.location.pathname === '/oauth-callback') return <OAuthCallback />;
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;






























































// import React, { useState, useEffect } from 'react';
// import { AuthProvider, useAuthContext } from './context/AuthContext';
// import { useApi } from './hooks/useApi';
// import { useLocalStorage } from './hooks/useLocalStorage';
// import { ROUTES } from './utils/constants';
// import AdminDashboard from './pages/AdminDashboard/AdminDashboard.jsx';
// import apiService from './services/api';

// // Components
// import Header from './components/common/Header/Header';
// import Footer from './components/common/Footer/Footer';
// import ChatWidget from './components/common/ChatWidget/ChatWidget';
// import StoriesSection from './components/StoriesSection/StoriesSection';

// // Modals
// import LoginModal from './components/modals/LoginModal/LoginModal';
// import VerificationModal from './components/modals/VerificationModal/VerificationModal';
// // ⛔ JOBS DISABLED: import PostJobModal from './components/modals/PostJobModal/PostJobModal';
// import ProductDetailModal from './components/modals/ProductDetailModal/ProductDetailModal';
// import OAuthCallback from './components/modals/OAuthCallback/OAuthCallback';

// // ✅ NEW: PostAdFlow replaces PostAdModal + PremiumPlansPage combo
// import PostAdFlow from './components/modals/PostAdModal/PostAdFlow';

// // Pages
// import HomePage from './pages/HomePage/HomePage';
// import ShopsPage from './pages/ShopsPage/ShopsPage';
// // ⛔ JOBS DISABLED: import JobsPage from './pages/JobsPage/JobsPage';
// import PlayToWinPage from './pages/PlayToWinPage/PlayToWinPage';
// import WalletPage from './pages/WalletPage/WalletPage';
// import ProfilePage from './pages/ProfilePage/ProfilePage';

// // Assets
// import logo from './assets/logo.png';
// import headerBg from './assets/header_background.png.png';

// // Styles
// import './App.css';

// // ── Constants ──────────────────────────────────────────────────────────────────
// const VALID_ROUTES = Object.values(ROUTES);
// const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// // ── Capture ?ad= IMMEDIATELY at module load, before any React renders ──────────
// // React StrictMode double-mounts, useLocalStorage re-renders, and effects all
// // run AFTER this line — so this is the only safe place to read the URL param.
// const INITIAL_AD_ID = new URLSearchParams(window.location.search).get('ad');

// // Module-level flag — survives React StrictMode's double unmount/remount
// let urlRestoredFlag = false;

// // ── URL helpers ────────────────────────────────────────────────────────────────
// const getAdIdFromUrl = () =>
//   new URLSearchParams(window.location.search).get('ad');

// const slugify = (text) =>
//   String(text || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// const pushAdToUrl = (adId, productName) => {
//   const url = new URL(window.location.href);
//   url.searchParams.set('ad', adId);
//   if (productName) url.searchParams.set('name', slugify(productName));
//   window.history.pushState({ adId }, '', url.toString());
// };

// const clearAdFromUrl = () => {
//   const url = new URL(window.location.href);
//   url.searchParams.delete('ad');
//   url.searchParams.delete('name');
//   window.history.replaceState({}, '', url.toString());
// };

// // Cleans up ?profile_tab= when leaving the profile page
// const clearProfileTabFromUrl = () => {
//   const url = new URL(window.location.href);
//   url.searchParams.delete('profile_tab');
//   window.history.replaceState({}, '', url.toString());
// };

// // ── Recommendation API helper ──────────────────────────────────────────────────
// async function fetchRecommendedAds() {
//   try {
//     const token =
//       localStorage.getItem('token') ||
//       localStorage.getItem('token')     ||
//       sessionStorage.getItem('authToken') ||
//       sessionStorage.getItem('token')   ||
//       '';

//     if (!token) return null;

//     const response = await fetch(`${API_BASE}/ads/recommended/`, {
//       headers: { 'Authorization': `Token ${token}` },
//       cache: 'no-store',
//     });

//     if (!response.ok) {
//       console.warn(`⚠️ /ads/recommended/ returned ${response.status} — falling back to /ads/`);
//       return null;
//     }

//     const feedType = response.headers.get('X-Feed-Type') || 'unknown';
//     const feedUser = response.headers.get('X-Feed-User') || 'unknown';
//     const feedCats = response.headers.get('X-Feed-Categories') || 'none';

//     const data = await response.json();
//     const raw  = Array.isArray(data) ? data
//                : data?.results        ? data.results
//                : data?.ads            ? data.ads
//                : null;

//     if (!raw) { console.warn('⚠️ /ads/recommended/ returned unexpected shape — falling back'); return null; }

//     return raw.map(ad => apiService.transformAdData(ad));
//   } catch (err) {
//     console.warn('⚠️ fetchRecommendedAds error — falling back to /ads/:', err.message);
//     return null;
//   }
// }

// // ── AppContent ─────────────────────────────────────────────────────────────────
// function AppContent() {
//   const { isAuthenticated, isAdmin, logout, authVersion } = useAuthContext();

//   const [currentPage, setCurrentPage] = useLocalStorage('currentPage', ROUTES.HOME);
//   const [products, setProducts] = useState([]);
//   // ⛔ JOBS DISABLED: const [jobs, setJobs] = useState([]);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [activeCategory, setActiveCategory] = useState('All');

//   const [loginModalOpen,        setLoginModalOpen]        = useState(false);
//   const [verificationModalOpen, setVerificationModalOpen] = useState(false);
//   // ⛔ JOBS DISABLED: const [postJobModalOpen, setPostJobModalOpen] = useState(false);
//   // ⚡ Open modal shell immediately if URL has ?ad= — shows skeleton while product loads
//   const [productDetailOpen,     setProductDetailOpen]     = useState(!!INITIAL_AD_ID);
//   const [selectedProduct,       setSelectedProduct]       = useState(null);
//   const [loginIdentifier,       setLoginIdentifier]       = useState('');

//   // ✅ NEW: Single boolean controls the entire Post Ad flow (plan picker → form)
//   const [postAdFlowOpen, setPostAdFlowOpen] = useState(false);
//   // ✅ Skeleton loading state — true while modal is open but product not yet loaded
//   const [isModalLoading, setIsModalLoading] = useState(!!INITIAL_AD_ID);

//   const { loading, fetchAds } = useApi(); // ⛔ JOBS DISABLED: fetchJobs removed

//   const [initialLoadDone, setInitialLoadDone] = useState(false); // ✅ useState so effects re-run when data loads

//   // ── Guard invalid stored route ─────────────────────────────────────────────
//   useEffect(() => {
//     if (!VALID_ROUTES.includes(currentPage)) setCurrentPage(ROUTES.HOME);
//   }, [currentPage, setCurrentPage]);

//   // ── Suspension check — runs on mount, focus, and tab visibility change ─────
//   // Independent of Header so it works on every page including Admin dashboard.
//   // If is_active === false the account was suspended — clear session immediately.
//   useEffect(() => {
//     const checkSuspension = async () => {
//       if (!isAuthenticated) return;
//       const loginTime = parseInt(localStorage.getItem('loginTime') || '0');
//       const token =
//         localStorage.getItem('token') ||
//         localStorage.getItem('authToken') || '';
//       if (!token) return;
//       try {
//         const res = await fetch(`${API_BASE}/auth/profile/`, {
//           headers: { Authorization: `Token ${token}` },
//         });

//         // ✅ KEY FIX: Django's TokenAuthentication returns 401 when is_active=False.
//         // The profile endpoint never returns is_active=false in the body because
//         // the request is rejected before it reaches the view.
//         // So we treat 401 = suspended/invalid session → force logout.
//         if (res.status === 401 || res.status === 403) {
//           console.warn('🔒 Session rejected (401/403) — account suspended or token invalid, forcing logout.');
//           logout();
//           setCurrentPage(ROUTES.HOME);
//           return;
//         }

//         if (res.ok) {
//           const data = await res.json();
//           // Belt-and-suspenders: also check is_active in body in case
//           // a custom auth backend allows the request through
//           if (data.is_active === false) {
//             console.warn('🔒 Account suspended (is_active=false) — forcing logout.');
//             logout();
//             setCurrentPage(ROUTES.HOME);
//           }
//         }
//       } catch (err) {
//         console.warn('Suspension check failed (non-fatal):', err.message);
//       }
//     };

//     // 1. Run on mount / whenever auth changes
//     setTimeout(checkSuspension, 5000);

//     // 2. Run when user returns to this window from another app
//     window.addEventListener('focus', checkSuspension);

//     // 3. Run when user switches back to this tab
//     const onVisibility = () => {
//       if (document.visibilityState === 'visible') checkSuspension();
//     };
//     document.addEventListener('visibilitychange', onVisibility);

//     return () => {
//       window.removeEventListener('focus', checkSuspension);
//       document.removeEventListener('visibilitychange', onVisibility);
//     };
//   }, [isAuthenticated, logout, setCurrentPage]);

//   // ── Clean up URL params when switching pages ───────────────────────────────
//   useEffect(() => {
//     if (currentPage !== ROUTES.PROFILE) {
//       clearProfileTabFromUrl(); // leaving profile → remove ?profile_tab=
//     }
//     if (currentPage !== ROUTES.HOME && !productDetailOpen) {
//       clearAdFromUrl(); // leaving home with no modal → remove ?ad= & ?name=
//     }
//   }, [currentPage]);

//   // ── Open modal skeleton IMMEDIATELY if URL has ?ad= ────────────────────────
//   // Step 1: open skeleton modal right away — user sees it instantly (zero wait)
//   // Step 2: fetch ONLY that one ad via /api/ads/<id>/ — 1 record, very fast
//   // Step 3: loadData() runs in parallel for the full feed (doesn't block modal)
//   // If the direct fetch fails/times out the URL restoration effect picks it up
//   // from the feed once loadData() finishes.
//   useEffect(() => {

//     if (INITIAL_AD_ID && !urlRestoredFlag) {
//       // ── Open skeleton right away ──────────────────────────────────────────
//       setIsModalLoading(true);
//       setProductDetailOpen(true);

//       // ── Fetch single ad directly via /api/ads/<id>/ ───────────────────────
//       // This hits a single-record endpoint and is much faster than the full feed.
//       const token =
//         localStorage.getItem('token') ||
//         localStorage.getItem('token')     ||
//         sessionStorage.getItem('authToken') ||
//         sessionStorage.getItem('token')   || '';

//       const headers = token ? { 'Authorization': `Token ${token}` } : {};

//       const singleAdFetch = fetch(`${API_BASE}/ads/${INITIAL_AD_ID}/`, { headers })
//         .then(r => r.ok ? r.json() : null)
//         .then(data => data ? apiService.transformAdData(data) : null);

//       const timeout = new Promise(resolve => setTimeout(() => resolve(null), 4000));

//       Promise.race([singleAdFetch, timeout]).then(product => {
//         if (product && !urlRestoredFlag) {
//           setSelectedProduct(product);
//           setIsModalLoading(false);
//           pushAdToUrl(INITIAL_AD_ID, product.name || product.title);
//           urlRestoredFlag = true;
//         } else if (!product) {
//           console.warn('⚠️ Direct fetch timed out or failed — skeleton stays until feed loads');
//         }
//       }).catch(() => {
//         console.warn('⚠️ Direct fetch error — skeleton stays until feed loads');
//       });

//       // ── Load full feed in parallel (does NOT block modal) ─────────────────
//       loadData();

//     } else {
//       loadData();
//     }
//   }, []);

//   // ── Re-fetch ads when auth changes ────────────────────────────────────────
//   useEffect(() => {
//     if (authVersion === undefined || authVersion === 0) return;
//     const refetch = async () => {
//       try {
//         if (isAuthenticated) {
//           const recommended = await fetchRecommendedAds();
//           if (recommended && recommended.length > 0) {
//             const _s2 = new Set();
//             setProducts(recommended.filter(a => { if (_s2.has(a.id)) return false; _s2.add(a.id); return true; }));
//             return;
//           }
//         }
//         const adsData = await fetchAds();
//         const _s3 = new Set();
//         setProducts(adsData.filter(a => { if (_s3.has(a.id)) return false; _s3.add(a.id); return true; }));
//       } catch (err) {
//         console.error('❌ Re-fetch after auth change failed:', err);
//       }
//     };
//     setTimeout(refetch, 800);
//   }, [authVersion, isAuthenticated]);

//   // ── Restore product modal from ?ad= URL ───────────────────────────────────
//   // Uses INITIAL_AD_ID captured at module load — NOT getAdIdFromUrl() inside
//   // the effect — because by the time effects run, React StrictMode double-mount
//   // and useLocalStorage re-renders may have already wiped window.location.search.
//   useEffect(() => {

//     if (urlRestoredFlag || !initialLoadDone) {
//       return;
//     }

//     if (products.length > 0) {
//       const product = products.find(p => String(p.id) === String(INITIAL_AD_ID));
//       if (product) {
//         setSelectedProduct(product); setProductDetailOpen(true);
//         setIsModalLoading(false); // ✅ hide skeleton
//         pushAdToUrl(INITIAL_AD_ID, product.name || product.title); // re-stamp URL with name slug
//         urlRestoredFlag = true; return;
//       } else {
//       }
//     }
//     fetchProductFromApi(INITIAL_AD_ID);
//   }, [products.length, initialLoadDone]);

//   const fetchProductFromApi = async (adId) => {
//     try {
//       const transformedData = await apiService.getAdById(adId);
//       if (!transformedData) { clearAdFromUrl(); setProductDetailOpen(false); setIsModalLoading(false); urlRestoredFlag = true; return; }
//       setSelectedProduct(transformedData); setProductDetailOpen(true);
//       setIsModalLoading(false); // ✅ hide skeleton
//       urlRestoredFlag = true;
//     } catch (error) {
//       console.error('❌ Error fetching product:', error);
//       clearAdFromUrl(); setProductDetailOpen(false); setIsModalLoading(false); urlRestoredFlag = true;
//     }
//   };

//   useEffect(() => {
//     const onPopState = (e) => {
//       const adId = getAdIdFromUrl();
//       if (!adId) { setProductDetailOpen(false); setSelectedProduct(null); }
//       else if (e.state?.adId) {
//         const product = products.find(p => String(p.id) === String(e.state.adId));
//         if (product) { setSelectedProduct(product); setProductDetailOpen(true); }
//         else fetchProductFromApi(e.state.adId);
//       }
//     };
//     window.addEventListener('popstate', onPopState);
//     return () => window.removeEventListener('popstate', onPopState);
//   }, [products]);

//   // ── Data loader ────────────────────────────────────────────────────────────
//   const loadData = async () => {
//     try {

//       // ⚡ Load ads feed (Jobs disabled)
//       const adsData = await (async () => {
//         const recommended = await fetchRecommendedAds();
//         if (recommended && recommended.length > 0) {
//           return recommended;
//         }
//         const ads = await fetchAds();
//         return ads;
//       })();

//       const _seen = new Set();
//       setProducts(adsData.filter(ad => { if (_seen.has(ad.id)) return false; _seen.add(ad.id); return true; }));
//       // ⛔ JOBS DISABLED: setJobs(jobsData);
//       setInitialLoadDone(true);
//       return adsData;
//     } catch (error) {
//       console.error('❌ Error loading data:', error);
//       setInitialLoadDone(true); // ✅ FIX: even on error, allow URL restoration to attempt
//     }
//   };

//   // ── Modal handlers ─────────────────────────────────────────────────────────
//   const openLoginModal        = () => setLoginModalOpen(true);
//   const closeLoginModal       = () => setLoginModalOpen(false);

//   const openVerificationModal = (identifier) => {
//     setLoginIdentifier(identifier);
//     setVerificationModalOpen(true);
//   };
//   const closeVerificationModal = () => setVerificationModalOpen(false);

//   // ✅ UPDATED: openPostAdModal now opens PostAdFlow directly
//   // PostAdFlow handles the two steps internally:
//   //   Step 1 — PlanSelectionModal (with promo badges)
//   //   Step 2 — PostAdModal (form, with ← Change Plan button)
//   const openPostAdModal = () => {
//     if (!isAuthenticated) { openLoginModal(); return; }
//     setPostAdFlowOpen(true);
//   };

//   // ⛔ JOBS DISABLED: const openPostJobModal = () => { ... };
//   // ⛔ JOBS DISABLED: const closePostJobModal = () => setPostJobModalOpen(false);

//   const openProductDetail = (product) => {
//     setSelectedProduct(product); setProductDetailOpen(true);
//     pushAdToUrl(product.id, product.name || product.title);
//   };
//   const closeProductDetail = () => {
//     setProductDetailOpen(false); setSelectedProduct(null); clearAdFromUrl();
//   };
//   const openProductById = (productId) => {
//     const product = products.find(p => p.id === productId);
//     if (product) openProductDetail(product);
//   };

//   const handleSearchFocus  = () => document.querySelector('[data-category-section]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
//   const handleScrollToTop  = () => window.scrollTo({ top: 0, behavior: 'smooth' });
//   const getRelatedProducts = (currentProduct) => !currentProduct ? [] : products.filter(p => p.category === currentProduct.category && p.id !== currentProduct.id).slice(0, 6);
//   const handleAdminLogout  = () => { logout(); setCurrentPage(ROUTES.HOME); };

//   // ── Render ─────────────────────────────────────────────────────────────────
//   return (
//     <div className="App" style={{ position: 'relative' }}>
//       {currentPage !== ROUTES.ADMIN && (
//         <Header
//           logo={logo}
//           searchTerm={searchTerm}
//           setSearchTerm={setSearchTerm}
//           handleSearchFocus={handleSearchFocus}
//           handleScrollToTop={handleScrollToTop}
//           currentPage={currentPage}
//           setCurrentPage={setCurrentPage}
//           openLoginModal={openLoginModal}
//           openPostAdModal={openPostAdModal}
//         />
//       )}

//       {currentPage === ROUTES.HOME && <StoriesSection />}

//       {currentPage === ROUTES.HOME && (
//         <HomePage
//           products={products}
//           loading={loading}
//           searchTerm={searchTerm}
//           activeCategory={activeCategory}
//           setActiveCategory={setActiveCategory}
//           handleSearchFocus={handleSearchFocus}
//           openProductDetail={openProductDetail}
//           openPostAdModal={openPostAdModal}
//           headerBg={headerBg}
//         />
//       )}
//       {currentPage === ROUTES.SHOPS      && <ShopsPage />}
//       {/* ⛔ JOBS DISABLED: currentPage === ROUTES.JOBS && <JobsPage jobs={jobs} openPostJobModal={openPostJobModal} /> */}
//       {currentPage === ROUTES.PLAY_TO_WIN && <PlayToWinPage />}
//       {currentPage === ROUTES.WALLET     && <WalletPage />}
//       {currentPage === ROUTES.PROFILE    && <ProfilePage />}
//       {currentPage === ROUTES.ADMIN && isAdmin && (
//         <AdminDashboard onLogout={handleAdminLogout} />
//       )}

//       {/* ✅ NEW: PostAdFlow — single component handles plan selection + ad form */}
//       <PostAdFlow
//         isOpen={postAdFlowOpen}
//         onClose={() => setPostAdFlowOpen(false)}
//         onAdPosted={loadData}
//         noTrigger
//       />

//       <LoginModal
//         isOpen={loginModalOpen}
//         onClose={closeLoginModal}
//         onOpenVerification={openVerificationModal}
//         setCurrentPage={setCurrentPage}
//       />

//       <VerificationModal
//         isOpen={verificationModalOpen}
//         onClose={closeVerificationModal}
//         loginIdentifier={loginIdentifier}
//         setLoginIdentifier={setLoginIdentifier}
//         setCurrentPage={setCurrentPage}
//       />

//       {/* ⛔ JOBS DISABLED: */}
//       {/* ⛔ JOBS DISABLED: <PostJobModal isOpen={postJobModalOpen} onClose={closePostJobModal} onJobPosted={loadData} /> */}
     

//       <ProductDetailModal
//         isOpen={productDetailOpen}
//         product={selectedProduct}
//         isLoading={isModalLoading}
//         onClose={closeProductDetail}
//         getRelatedProducts={getRelatedProducts}
//         onProductClick={openProductDetail}
//         isLoggedIn={isAuthenticated}
//         onOpenLoginModal={openLoginModal}
//       />

//       {currentPage !== ROUTES.ADMIN && (
//         <>
//           <ChatWidget />
//           <Footer />
//         </>
//       )}
//     </div>
//   );
// }

// // ── App root ───────────────────────────────────────────────────────────────────
// function App() {
//   if (window.location.pathname === '/oauth-callback') return <OAuthCallback />;
//   return (
//     <AuthProvider>
//       <AppContent />
//     </AuthProvider>
//   );
// }

// export default App;





































































// // import React, { useState, useEffect } from 'react';
// // import { AuthProvider, useAuthContext } from './context/AuthContext';
// // import { useApi } from './hooks/useApi';
// // import { useLocalStorage } from './hooks/useLocalStorage';
// // import { ROUTES } from './utils/constants';
// // import AdminDashboard from './pages/AdminDashboard/AdminDashboard.jsx';
// // import apiService from './services/api';

// // // Components
// // import Header from './components/common/Header/Header';
// // import Footer from './components/common/Footer/Footer';
// // import ChatWidget from './components/common/ChatWidget/ChatWidget';
// // import StoriesSection from './components/StoriesSection/StoriesSection';

// // // Modals
// // import LoginModal from './components/modals/LoginModal/LoginModal';
// // import VerificationModal from './components/modals/VerificationModal/VerificationModal';
// // import PostJobModal from './components/modals/PostJobModal/PostJobModal';
// // import ProductDetailModal from './components/modals/ProductDetailModal/ProductDetailModal';
// // import OAuthCallback from './components/modals/OAuthCallback/OAuthCallback';

// // // ✅ NEW: PostAdFlow replaces PostAdModal + PremiumPlansPage combo
// // import PostAdFlow from './components/modals/PostAdModal/PostAdFlow';

// // // Pages
// // import HomePage from './pages/HomePage/HomePage';
// // import ShopsPage from './pages/ShopsPage/ShopsPage';
// // import JobsPage from './pages/JobsPage/JobsPage';
// // import PlayToWinPage from './pages/PlayToWinPage/PlayToWinPage';
// // import WalletPage from './pages/WalletPage/WalletPage';
// // import ProfilePage from './pages/ProfilePage/ProfilePage';

// // // Assets
// // import logo from './assets/logo.png';
// // import headerBg from './assets/header_background.png.png';

// // // Styles
// // import './App.css';

// // // ── Constants ──────────────────────────────────────────────────────────────────
// // const VALID_ROUTES = Object.values(ROUTES);
// // const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// // // ── Capture ?ad= IMMEDIATELY at module load, before any React renders ──────────
// // // React StrictMode double-mounts, useLocalStorage re-renders, and effects all
// // // run AFTER this line — so this is the only safe place to read the URL param.
// // const INITIAL_AD_ID = new URLSearchParams(window.location.search).get('ad');

// // // Module-level flag — survives React StrictMode's double unmount/remount
// // let urlRestoredFlag = false;

// // // ── URL helpers ────────────────────────────────────────────────────────────────
// // const getAdIdFromUrl = () =>
// //   new URLSearchParams(window.location.search).get('ad');

// // const slugify = (text) =>
// //   String(text || '').toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

// // const pushAdToUrl = (adId, productName) => {
// //   const url = new URL(window.location.href);
// //   url.searchParams.set('ad', adId);
// //   if (productName) url.searchParams.set('name', slugify(productName));
// //   window.history.pushState({ adId }, '', url.toString());
// // };

// // const clearAdFromUrl = () => {
// //   const url = new URL(window.location.href);
// //   url.searchParams.delete('ad');
// //   url.searchParams.delete('name');
// //   window.history.replaceState({}, '', url.toString());
// // };

// // // Cleans up ?profile_tab= when leaving the profile page
// // const clearProfileTabFromUrl = () => {
// //   const url = new URL(window.location.href);
// //   url.searchParams.delete('profile_tab');
// //   window.history.replaceState({}, '', url.toString());
// // };

// // // ── Recommendation API helper ──────────────────────────────────────────────────
// // async function fetchRecommendedAds() {
// //   try {
// //     const token =
// //       localStorage.getItem('token') ||
// //       localStorage.getItem('token')     ||
// //       sessionStorage.getItem('authToken') ||
// //       sessionStorage.getItem('token')   ||
// //       '';

// //     if (!token) return null;

// //     const response = await fetch(`${API_BASE}/ads/recommended/`, {
// //       headers: { 'Authorization': `Token ${token}` },
// //       cache: 'no-store',
// //     });

// //     if (!response.ok) {
// //       console.warn(`⚠️ /ads/recommended/ returned ${response.status} — falling back to /ads/`);
// //       return null;
// //     }

// //     const feedType = response.headers.get('X-Feed-Type') || 'unknown';
// //     const feedUser = response.headers.get('X-Feed-User') || 'unknown';
// //     const feedCats = response.headers.get('X-Feed-Categories') || 'none';

// //     const data = await response.json();
// //     const raw  = Array.isArray(data) ? data
// //                : data?.results        ? data.results
// //                : data?.ads            ? data.ads
// //                : null;

// //     if (!raw) { console.warn('⚠️ /ads/recommended/ returned unexpected shape — falling back'); return null; }

// //     return raw.map(ad => apiService.transformAdData(ad));
// //   } catch (err) {
// //     console.warn('⚠️ fetchRecommendedAds error — falling back to /ads/:', err.message);
// //     return null;
// //   }
// // }

// // // ── AppContent ─────────────────────────────────────────────────────────────────
// // function AppContent() {
// //   const { isAuthenticated, isAdmin, logout, authVersion } = useAuthContext();

// //   const [currentPage, setCurrentPage] = useLocalStorage('currentPage', ROUTES.HOME);
// //   const [products, setProducts] = useState([]);
// //   const [jobs, setJobs] = useState([]);
// //   const [searchTerm, setSearchTerm] = useState('');
// //   const [activeCategory, setActiveCategory] = useState('All');

// //   const [loginModalOpen,        setLoginModalOpen]        = useState(false);
// //   const [verificationModalOpen, setVerificationModalOpen] = useState(false);
// //   const [postJobModalOpen,      setPostJobModalOpen]      = useState(false);
// //   // ⚡ Open modal shell immediately if URL has ?ad= — shows skeleton while product loads
// //   const [productDetailOpen,     setProductDetailOpen]     = useState(!!INITIAL_AD_ID);
// //   const [selectedProduct,       setSelectedProduct]       = useState(null);
// //   const [loginIdentifier,       setLoginIdentifier]       = useState('');

// //   // ✅ NEW: Single boolean controls the entire Post Ad flow (plan picker → form)
// //   const [postAdFlowOpen, setPostAdFlowOpen] = useState(false);
// //   // ✅ Skeleton loading state — true while modal is open but product not yet loaded
// //   const [isModalLoading, setIsModalLoading] = useState(!!INITIAL_AD_ID);

// //   const { loading, fetchAds, fetchJobs } = useApi();

// //   const [initialLoadDone, setInitialLoadDone] = useState(false); // ✅ useState so effects re-run when data loads

// //   // ── Guard invalid stored route ─────────────────────────────────────────────
// //   useEffect(() => {
// //     if (!VALID_ROUTES.includes(currentPage)) setCurrentPage(ROUTES.HOME);
// //   }, [currentPage, setCurrentPage]);

// //   // ── Suspension check — runs on mount, focus, and tab visibility change ─────
// //   // Independent of Header so it works on every page including Admin dashboard.
// //   // If is_active === false the account was suspended — clear session immediately.
// //   useEffect(() => {
// //     const checkSuspension = async () => {
// //       if (!isAuthenticated) return;
// //       const loginTime = parseInt(localStorage.getItem('loginTime') || '0');
// //       const token =
// //         localStorage.getItem('token') ||
// //         localStorage.getItem('authToken') || '';
// //       if (!token) return;
// //       try {
// //         const res = await fetch(`${API_BASE}/auth/profile/`, {
// //           headers: { Authorization: `Token ${token}` },
// //         });

// //         // ✅ KEY FIX: Django's TokenAuthentication returns 401 when is_active=False.
// //         // The profile endpoint never returns is_active=false in the body because
// //         // the request is rejected before it reaches the view.
// //         // So we treat 401 = suspended/invalid session → force logout.
// //         if (res.status === 401 || res.status === 403) {
// //           console.warn('🔒 Session rejected (401/403) — account suspended or token invalid, forcing logout.');
// //           logout();
// //           setCurrentPage(ROUTES.HOME);
// //           return;
// //         }

// //         if (res.ok) {
// //           const data = await res.json();
// //           // Belt-and-suspenders: also check is_active in body in case
// //           // a custom auth backend allows the request through
// //           if (data.is_active === false) {
// //             console.warn('🔒 Account suspended (is_active=false) — forcing logout.');
// //             logout();
// //             setCurrentPage(ROUTES.HOME);
// //           }
// //         }
// //       } catch (err) {
// //         console.warn('Suspension check failed (non-fatal):', err.message);
// //       }
// //     };

// //     // 1. Run on mount / whenever auth changes
// //     setTimeout(checkSuspension, 5000);

// //     // 2. Run when user returns to this window from another app
// //     window.addEventListener('focus', checkSuspension);

// //     // 3. Run when user switches back to this tab
// //     const onVisibility = () => {
// //       if (document.visibilityState === 'visible') checkSuspension();
// //     };
// //     document.addEventListener('visibilitychange', onVisibility);

// //     return () => {
// //       window.removeEventListener('focus', checkSuspension);
// //       document.removeEventListener('visibilitychange', onVisibility);
// //     };
// //   }, [isAuthenticated, logout, setCurrentPage]);

// //   // ── Clean up URL params when switching pages ───────────────────────────────
// //   useEffect(() => {
// //     if (currentPage !== ROUTES.PROFILE) {
// //       clearProfileTabFromUrl(); // leaving profile → remove ?profile_tab=
// //     }
// //     if (currentPage !== ROUTES.HOME && !productDetailOpen) {
// //       clearAdFromUrl(); // leaving home with no modal → remove ?ad= & ?name=
// //     }
// //   }, [currentPage]);

// //   // ── Open modal skeleton IMMEDIATELY if URL has ?ad= ────────────────────────
// //   // Step 1: open skeleton modal right away — user sees it instantly (zero wait)
// //   // Step 2: fetch ONLY that one ad via /api/ads/<id>/ — 1 record, very fast
// //   // Step 3: loadData() runs in parallel for the full feed (doesn't block modal)
// //   // If the direct fetch fails/times out the URL restoration effect picks it up
// //   // from the feed once loadData() finishes.
// //   useEffect(() => {

// //     if (INITIAL_AD_ID && !urlRestoredFlag) {
// //       // ── Open skeleton right away ──────────────────────────────────────────
// //       setIsModalLoading(true);
// //       setProductDetailOpen(true);

// //       // ── Fetch single ad directly via /api/ads/<id>/ ───────────────────────
// //       // This hits a single-record endpoint and is much faster than the full feed.
// //       const token =
// //         localStorage.getItem('token') ||
// //         localStorage.getItem('token')     ||
// //         sessionStorage.getItem('authToken') ||
// //         sessionStorage.getItem('token')   || '';

// //       const headers = token ? { 'Authorization': `Token ${token}` } : {};

// //       const singleAdFetch = fetch(`${API_BASE}/ads/${INITIAL_AD_ID}/`, { headers })
// //         .then(r => r.ok ? r.json() : null)
// //         .then(data => data ? apiService.transformAdData(data) : null);

// //       const timeout = new Promise(resolve => setTimeout(() => resolve(null), 4000));

// //       Promise.race([singleAdFetch, timeout]).then(product => {
// //         if (product && !urlRestoredFlag) {
// //           setSelectedProduct(product);
// //           setIsModalLoading(false);
// //           pushAdToUrl(INITIAL_AD_ID, product.name || product.title);
// //           urlRestoredFlag = true;
// //         } else if (!product) {
// //           console.warn('⚠️ Direct fetch timed out or failed — skeleton stays until feed loads');
// //         }
// //       }).catch(() => {
// //         console.warn('⚠️ Direct fetch error — skeleton stays until feed loads');
// //       });

// //       // ── Load full feed in parallel (does NOT block modal) ─────────────────
// //       loadData();

// //     } else {
// //       loadData();
// //     }
// //   }, []);

// //   // ── Re-fetch ads when auth changes ────────────────────────────────────────
// //   useEffect(() => {
// //     if (authVersion === undefined || authVersion === 0) return;
// //     const refetch = async () => {
// //       try {
// //         if (isAuthenticated) {
// //           const recommended = await fetchRecommendedAds();
// //           if (recommended && recommended.length > 0) {
// //             const _s2 = new Set();
// //             setProducts(recommended.filter(a => { if (_s2.has(a.id)) return false; _s2.add(a.id); return true; }));
// //             return;
// //           }
// //         }
// //         const adsData = await fetchAds();
// //         const _s3 = new Set();
// //         setProducts(adsData.filter(a => { if (_s3.has(a.id)) return false; _s3.add(a.id); return true; }));
// //       } catch (err) {
// //         console.error('❌ Re-fetch after auth change failed:', err);
// //       }
// //     };
// //     setTimeout(refetch, 800);
// //   }, [authVersion, isAuthenticated]);

// //   // ── Restore product modal from ?ad= URL ───────────────────────────────────
// //   // Uses INITIAL_AD_ID captured at module load — NOT getAdIdFromUrl() inside
// //   // the effect — because by the time effects run, React StrictMode double-mount
// //   // and useLocalStorage re-renders may have already wiped window.location.search.
// //   useEffect(() => {

// //     if (urlRestoredFlag || !initialLoadDone) {
// //       return;
// //     }

// //     if (products.length > 0) {
// //       const product = products.find(p => String(p.id) === String(INITIAL_AD_ID));
// //       if (product) {
// //         setSelectedProduct(product); setProductDetailOpen(true);
// //         setIsModalLoading(false); // ✅ hide skeleton
// //         pushAdToUrl(INITIAL_AD_ID, product.name || product.title); // re-stamp URL with name slug
// //         urlRestoredFlag = true; return;
// //       } else {
// //       }
// //     }
// //     fetchProductFromApi(INITIAL_AD_ID);
// //   }, [products.length, initialLoadDone]);

// //   const fetchProductFromApi = async (adId) => {
// //     try {
// //       const transformedData = await apiService.getAdById(adId);
// //       if (!transformedData) { clearAdFromUrl(); setProductDetailOpen(false); setIsModalLoading(false); urlRestoredFlag = true; return; }
// //       setSelectedProduct(transformedData); setProductDetailOpen(true);
// //       setIsModalLoading(false); // ✅ hide skeleton
// //       urlRestoredFlag = true;
// //     } catch (error) {
// //       console.error('❌ Error fetching product:', error);
// //       clearAdFromUrl(); setProductDetailOpen(false); setIsModalLoading(false); urlRestoredFlag = true;
// //     }
// //   };

// //   useEffect(() => {
// //     const onPopState = (e) => {
// //       const adId = getAdIdFromUrl();
// //       if (!adId) { setProductDetailOpen(false); setSelectedProduct(null); }
// //       else if (e.state?.adId) {
// //         const product = products.find(p => String(p.id) === String(e.state.adId));
// //         if (product) { setSelectedProduct(product); setProductDetailOpen(true); }
// //         else fetchProductFromApi(e.state.adId);
// //       }
// //     };
// //     window.addEventListener('popstate', onPopState);
// //     return () => window.removeEventListener('popstate', onPopState);
// //   }, [products]);

// //   // ── Data loader ────────────────────────────────────────────────────────────
// //   const loadData = async () => {
// //     try {

// //       // ⚡ Run jobs + ads fetch in parallel — don't let one block the other
// //       const [jobsData, adsData] = await Promise.all([
// //         fetchJobs(),
// //         (async () => {
// //           const recommended = await fetchRecommendedAds();
// //           if (recommended && recommended.length > 0) {
// //             return recommended;
// //           }
// //           const ads = await fetchAds();
// //           return ads;
// //         })(),
// //       ]);

// //       const _seen = new Set();
// //       setProducts(adsData.filter(ad => { if (_seen.has(ad.id)) return false; _seen.add(ad.id); return true; }));
// //       setJobs(jobsData);
// //       setInitialLoadDone(true);
// //       return adsData;
// //     } catch (error) {
// //       console.error('❌ Error loading data:', error);
// //       setInitialLoadDone(true); // ✅ FIX: even on error, allow URL restoration to attempt
// //     }
// //   };

// //   // ── Modal handlers ─────────────────────────────────────────────────────────
// //   const openLoginModal        = () => setLoginModalOpen(true);
// //   const closeLoginModal       = () => setLoginModalOpen(false);

// //   const openVerificationModal = (identifier) => {
// //     setLoginIdentifier(identifier);
// //     setVerificationModalOpen(true);
// //   };
// //   const closeVerificationModal = () => setVerificationModalOpen(false);

// //   // ✅ UPDATED: openPostAdModal now opens PostAdFlow directly
// //   // PostAdFlow handles the two steps internally:
// //   //   Step 1 — PlanSelectionModal (with promo badges)
// //   //   Step 2 — PostAdModal (form, with ← Change Plan button)
// //   const openPostAdModal = () => {
// //     if (!isAuthenticated) { openLoginModal(); return; }
// //     setPostAdFlowOpen(true);
// //   };

// //   const openPostJobModal  = () => { if (!isAuthenticated) { openLoginModal(); return; } setPostJobModalOpen(true); };
// //   const closePostJobModal = () => setPostJobModalOpen(false);

// //   const openProductDetail = (product) => {
// //     setSelectedProduct(product); setProductDetailOpen(true);
// //     pushAdToUrl(product.id, product.name || product.title);
// //   };
// //   const closeProductDetail = () => {
// //     setProductDetailOpen(false); setSelectedProduct(null); clearAdFromUrl();
// //   };
// //   const openProductById = (productId) => {
// //     const product = products.find(p => p.id === productId);
// //     if (product) openProductDetail(product);
// //   };

// //   const handleSearchFocus  = () => document.querySelector('[data-category-section]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
// //   const handleScrollToTop  = () => window.scrollTo({ top: 0, behavior: 'smooth' });
// //   const getRelatedProducts = (currentProduct) => !currentProduct ? [] : products.filter(p => p.category === currentProduct.category && p.id !== currentProduct.id).slice(0, 6);
// //   const handleAdminLogout  = () => { logout(); setCurrentPage(ROUTES.HOME); };

// //   // ── Render ─────────────────────────────────────────────────────────────────
// //   return (
// //     <div className="App" style={{ position: 'relative' }}>
// //       {currentPage !== ROUTES.ADMIN && (
// //         <Header
// //           logo={logo}
// //           searchTerm={searchTerm}
// //           setSearchTerm={setSearchTerm}
// //           handleSearchFocus={handleSearchFocus}
// //           handleScrollToTop={handleScrollToTop}
// //           currentPage={currentPage}
// //           setCurrentPage={setCurrentPage}
// //           openLoginModal={openLoginModal}
// //           openPostAdModal={openPostAdModal}
// //         />
// //       )}

// //       {currentPage === ROUTES.HOME && <StoriesSection />}

// //       {currentPage === ROUTES.HOME && (
// //         <HomePage
// //           products={products}
// //           loading={loading}
// //           searchTerm={searchTerm}
// //           activeCategory={activeCategory}
// //           setActiveCategory={setActiveCategory}
// //           handleSearchFocus={handleSearchFocus}
// //           openProductDetail={openProductDetail}
// //           openPostAdModal={openPostAdModal}
// //           headerBg={headerBg}
// //         />
// //       )}
// //       {currentPage === ROUTES.SHOPS      && <ShopsPage />}
// //       {currentPage === ROUTES.JOBS       && <JobsPage jobs={jobs} openPostJobModal={openPostJobModal} />}
// //       {currentPage === ROUTES.PLAY_TO_WIN && <PlayToWinPage />}
// //       {currentPage === ROUTES.WALLET     && <WalletPage />}
// //       {currentPage === ROUTES.PROFILE    && <ProfilePage />}
// //       {currentPage === ROUTES.ADMIN && isAdmin && (
// //         <AdminDashboard onLogout={handleAdminLogout} />
// //       )}

// //       {/* ✅ NEW: PostAdFlow — single component handles plan selection + ad form */}
// //       <PostAdFlow
// //         isOpen={postAdFlowOpen}
// //         onClose={() => setPostAdFlowOpen(false)}
// //         onAdPosted={loadData}
// //         noTrigger
// //       />

// //       <LoginModal
// //         isOpen={loginModalOpen}
// //         onClose={closeLoginModal}
// //         onOpenVerification={openVerificationModal}
// //         setCurrentPage={setCurrentPage}
// //       />

// //       <VerificationModal
// //         isOpen={verificationModalOpen}
// //         onClose={closeVerificationModal}
// //         loginIdentifier={loginIdentifier}
// //         setLoginIdentifier={setLoginIdentifier}
// //         setCurrentPage={setCurrentPage}
// //       />

// //       <PostJobModal
// //         isOpen={postJobModalOpen}
// //         onClose={closePostJobModal}
// //         onJobPosted={loadData}
// //       />

// //       <ProductDetailModal
// //         isOpen={productDetailOpen}
// //         product={selectedProduct}
// //         isLoading={isModalLoading}
// //         onClose={closeProductDetail}
// //         getRelatedProducts={getRelatedProducts}
// //         onProductClick={openProductDetail}
// //         isLoggedIn={isAuthenticated}
// //         onOpenLoginModal={openLoginModal}
// //       />

// //       {currentPage !== ROUTES.ADMIN && (
// //         <>
// //           <ChatWidget />
// //           <Footer />
// //         </>
// //       )}
// //     </div>
// //   );
// // }

// // // ── App root ───────────────────────────────────────────────────────────────────
// // function App() {
// //   if (window.location.pathname === '/oauth-callback') return <OAuthCallback />;
// //   return (
// //     <AuthProvider>
// //       <AppContent />
// //     </AuthProvider>
// //   );
// // }

// // export default App;


